// The Workbox runtime cache of the employee read endpoints (name from vite.config.ts).
import type { QueryClient } from "@tanstack/react-query";
import type { WeekResponse } from "@zeiterfassung/shared";
import { get } from "@/api/client";
import { queryKeys } from "@/api/queries/keys";

const API_CACHE_PREFIX = "api-";
// The current runtime cache (`cacheName` in vite.config.ts); older ones hold responses of an
// earlier shape and are dropped at start.
const API_CACHE_NAME = "api-v2";
const warmedWeeks = new Set<string>();

// The week page asks for the *current* week without a parameter, the entry form for the same
// week by id. Seed the query cache and the service-worker cache with the explicit url so the
// form still works offline after a visit of the week page.
export function warmWeekCache(client: QueryClient, week: WeekResponse): void {
  client.setQueryData(queryKeys.week(week.week.id), week);
  if (typeof navigator === "undefined" || !navigator.serviceWorker?.controller) return;
  if (warmedWeeks.has(week.week.id)) return;
  warmedWeeks.add(week.week.id);
  void get(`/api/time-entries?week=${week.week.id}`).catch(() => warmedWeeks.delete(week.week.id));
}

// Deletes api caches of earlier builds (other names than the current one).
export async function dropStaleApiCaches(): Promise<void> {
  if (typeof caches === "undefined") return;
  try {
    const names = await caches.keys();
    await Promise.all(
      names
        .filter((n) => n.startsWith(API_CACHE_PREFIX) && n !== API_CACHE_NAME)
        .map((n) => caches.delete(n)),
    );
  } catch {
    // Cache storage can be blocked; nothing to clean then.
  }
}

// Drops the runtime api cache (on logout, so the next user never sees cached data).
export async function clearApiCache(): Promise<void> {
  if (typeof caches === "undefined") return;
  try {
    const names = await caches.keys();
    await Promise.all(
      names.filter((n) => n.startsWith(API_CACHE_PREFIX)).map((n) => caches.delete(n)),
    );
  } catch {
    // Cache storage can be blocked; nothing to clean then.
  }
}
